import { useCallback, useEffect, useState } from 'react';
import { Banknote, Link2, Plus, Smartphone, Trash2 } from 'lucide-react';
import { SettingsCard, SettingsSwitch, SettingsTextField } from './settingsUi.jsx';
import { getOrCreateDeviceId } from '../../offline/deviceIdentity';
import {
  formatDeviceDisplayName,
  getDeviceFriendlyLabel,
  inferDefaultDeviceLabel,
  setDeviceFriendlyLabel,
} from '../../utils/posDeviceDisplay';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../Toast';

export default function CaissePostesSettings({ commercantId, canManage = false }) {
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [postes, setPostes] = useState([]);
  const [singlePosteMode, setSinglePosteMode] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [enrollmentsByPoste, setEnrollmentsByPoste] = useState({});
  const [openSessionPosteIds, setOpenSessionPosteIds] = useState(() => new Set());
  const [deviceLabelDraft, setDeviceLabelDraft] = useState(
    () => getDeviceFriendlyLabel(getOrCreateDeviceId()) || inferDefaultDeviceLabel()
  );
  const localDeviceId = getOrCreateDeviceId();

  const load = useCallback(async () => {
    if (!commercantId) return;
    setLoading(true);
    try {
      const data = canManage
        ? await apiService.getCaissePostesAdmin(commercantId)
        : await apiService.getCaissePostes(commercantId);
      const rows = data.postes || [];
      setPostes(rows);
      setSinglePosteMode(Boolean(data.singlePosteMode));
      try {
        const sess = await apiService.getCaisseSession(commercantId);
        const ids = new Set((sess?.openSessions || []).map((s) => s.posteCaisseId));
        setOpenSessionPosteIds(ids);
      } catch {
        setOpenSessionPosteIds(new Set());
      }
      if (canManage && commercantId) {
        const map = {};
        await Promise.all(
          rows.map(async (p) => {
            try {
              const en = await apiService.getCaissePosteEnrollments(commercantId, p.id);
              map[p.id] = en.enrollments || [];
            } catch {
              map[p.id] = [];
            }
          })
        );
        setEnrollmentsByPoste(map);
      }
    } catch (err) {
      toast.error(err?.message || 'Impossible de charger les caisses');
    } finally {
      setLoading(false);
    }
  }, [commercantId, canManage, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const createPoste = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await apiService.createCaissePoste(commercantId, { label: newLabel.trim() });
      setNewLabel('');
      toast.success('Poste de caisse créé');
      await load();
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Création impossible');
    } finally {
      setSaving(false);
    }
  };

  const updatePoste = async (posteId, patch) => {
    setSaving(true);
    try {
      await apiService.updateCaissePoste(commercantId, posteId, patch);
      toast.success('Poste mis à jour');
      await load();
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Mise à jour impossible');
    } finally {
      setSaving(false);
    }
  };

  const deletePoste = async (posteId) => {
    if (!window.confirm('Supprimer ce poste ? Cette action est impossible s’il possède un historique.')) {
      return;
    }
    setSaving(true);
    try {
      await apiService.deleteCaissePoste(commercantId, posteId);
      toast.success('Poste supprimé');
      await load();
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Suppression impossible');
    } finally {
      setSaving(false);
    }
  };

  const enrollThisDevice = async (posteId) => {
    setSaving(true);
    try {
      setDeviceFriendlyLabel(localDeviceId, deviceLabelDraft.trim() || inferDefaultDeviceLabel());
      await apiService.enrollCaissePosteDevice(commercantId, posteId, localDeviceId);
      toast.success('Appareil associé au poste');
      await load();
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Association impossible');
    } finally {
      setSaving(false);
    }
  };

  const revokeDevice = async (deviceId) => {
    setSaving(true);
    try {
      await apiService.revokeCaissePosteDevice(commercantId, deviceId);
      toast.success('Appareil révoqué');
      await load();
    } catch (err) {
      toast.error(err?.data?.error?.message || err?.message || 'Révocation impossible');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="caisse-postes-panel">
    <SettingsCard
      icon={Banknote}
      title="Caisses"
      subtitle={
        singlePosteMode
          ? 'Un poste de caisse = une session financière. Plusieurs appareils (PC, iPad…) peuvent partager le même poste.'
          : 'Plusieurs postes financiers indépendants : fonds, sessions et clôtures séparés (activation contrôlée par Clickorico).'
      }
    >
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="space-y-4">
          {postes.map((poste) => (
            <div
              key={poste.id}
              data-testid={`caisse-postes-item-${poste.code}`}
              className="rounded-xl border border-gray-100 p-4 space-y-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{poste.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Code {poste.code}
                    {poste.code === 'PRINCIPALE' ? ' · poste principal' : ''}
                    {!poste.actif ? ' · désactivé' : ''}
                    {' · '}
                    {openSessionPosteIds.has(poste.id) ? (
                      <span className="text-emerald-700 font-medium">Session ouverte</span>
                    ) : (
                      <span>Session fermée</span>
                    )}
                  </p>
                </div>
                {canManage && poste.code !== 'PRINCIPALE' ? (
                  <div className="flex items-center gap-2">
                    <SettingsSwitch
                      checked={poste.actif !== false}
                      label={`Activer ${poste.label}`}
                      disabled={saving}
                      testId={`caisse-postes-active-switch-${poste.code}`}
                      onChange={(actif) => updatePoste(poste.id, { actif })}
                    />
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => deletePoste(poste.id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </button>
                  </div>
                ) : null}
              </div>

              {canManage ? (
                <div className="pt-1 border-t border-gray-50 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                  <Smartphone className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    Appareils rattachés : {poste.activeDeviceCount ?? 0}
                  </span>
                  <button
                    type="button"
                    data-testid={`caisse-postes-enroll-${poste.code}`}
                    disabled={saving || !poste.actif}
                    onClick={() => enrollThisDevice(poste.id)}
                    className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Associer cet appareil
                  </button>
                  </div>
                  {(enrollmentsByPoste[poste.id] || []).length > 0 ? (
                    <ul className="text-xs text-gray-600 space-y-1 pl-6 list-disc">
                      {(enrollmentsByPoste[poste.id] || []).map((en) => (
                        <li key={en.id}>
                          {formatDeviceDisplayName(en.deviceId)}
                          {en.deviceId === localDeviceId ? ' (cet appareil)' : ''}
                          <button
                            type="button"
                            className="ml-2 text-red-600 underline"
                            disabled={saving}
                            onClick={() => revokeDevice(en.deviceId)}
                          >
                            Révoquer
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400 pl-6">Aucun appareil rattaché.</p>
                  )}
                </div>
              ) : null}
            </div>
          ))}

          {canManage ? (
            <div className="rounded-xl border border-dashed border-gray-200 p-4 space-y-3">
              <p className="text-sm font-medium text-gray-800">Nouveau poste</p>
              <div className="flex flex-wrap gap-2 items-end">
                <SettingsTextField
                  compact
                  label="Nom (ex. Bar, Terrasse)"
                  value={newLabel}
                  onChange={setNewLabel}
                  testId="caisse-postes-new-label"
                />
                <button
                  type="button"
                  data-testid="caisse-postes-add"
                  disabled={saving || newLabel.trim().length < 2}
                  onClick={createPoste}
                  className="inline-flex items-center gap-1 rounded-xl bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter
                </button>
              </div>
              <SettingsTextField
                compact
                label="Nom de cet appareil (ex. iPad salle, PC comptoir)"
                value={deviceLabelDraft}
                onChange={setDeviceLabelDraft}
                testId="caisse-postes-device-label"
              />
              <p className="text-xs text-gray-500">
                Identifiant technique (lecture seule) :{' '}
                <code className="text-[11px] bg-gray-100 px-1 rounded">{localDeviceId}</code>
              </p>
              <button
                type="button"
                data-testid="caisse-postes-revoke-device"
                disabled={saving}
                onClick={() => revokeDevice(localDeviceId)}
                className="text-xs text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
              >
                Révoquer l’association de cet appareil
              </button>
            </div>
          ) : null}
        </div>
      )}
    </SettingsCard>
    </div>
  );
}
